import { Component, computed, inject, signal, OnDestroy, OnInit, effect, TemplateRef, ViewChild, ViewContainerRef, EmbeddedViewRef, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../../core/services/api.service';
import { CommonService } from '../../../core/services/common.service';
import { HeadlinePageService } from '../../../core/services/headline-page.service';
import { IconComponent } from '../../icons/icon.component';
import { IconComponentV2 } from '../icon-component/icon.component';
import { BreakpointService } from '../../../core/services/breakpoint.service';

export interface Headline {
  id: string;
  timeRange: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY';
  periodStart: string;
  periodEnd: string;
  headline: string;
  title: string;
  summary: string;
}

/**
 * Minimal view-model for a cron configuration used to compute the
 * "next execution" countdown shown on each headline.
 */
export interface CronNextRun {
  cronType: string;
  nextScheduledRun: string | null;
}

@Component({
  selector: 'ai-headlines',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    IconComponentV2,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if(isDenefitsSelected()) {
      @if(isLoading()) {
        <div class="group mb-4 [@media(max-height:620px)]:mb-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-900 via-purple-900 to-fuchsia-900 backdrop-blur-sm p-6 flex flex-col items-center justify-center gap-3 min-h-[160px]">
           <app-icon name="loader" [size]="24" class="animate-spin text-white/80" />
           <span class="text-white/60 text-sm font-medium tracking-wide">Analyzing headlines...</span>
        </div>
      } @else if(headlines().length > 0) {
      <div class="group mb-4 [@media(max-height:620px)]:mb-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-950 from-2% to-purple-900 to-98% shadow-xl"
      (mouseenter)="isPaused.set(true)"
      (mouseleave)="isPaused.set(false)"
      (touchstart)="onTouchStart($event)"
      (touchend)="onTouchEnd($event)"
      >
        <!-- Desktop Navigation Chevrons -->
        @if(!isMobile()) {
          <button (click)="prevHeadline(); $event.stopPropagation()" 
                  class="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 text-white/30 hover:text-white hover:bg-white/10 rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer">
            <app-icon-v2 name="ChevronLeft" [size]="20" />
          </button>
          <button (click)="nextHeadline(); $event.stopPropagation()" 
                  class="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 text-white/30 hover:text-white hover:bg-white/10 rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer">
            <app-icon-v2 name="ChevronRight" [size]="20" />
          </button>
        }

        <!-- Top-right: Next cron execution countdown (themed by time range) -->
        <div class="absolute top-3 right-3 z-10">
          @if(nextRunCountdown(); as countdown) {
            <div class="flex items-center gap-1.5 px-2.5 py-1 rounded-full border backdrop-blur-sm text-[10px] font-semibold tracking-wide shadow-sm"
                 [ngClass]="getTimeRangeColor(headlines()[currentHeadlineIndex()].timeRange)"
                 [title]="'Next ' + getTimePeriodDisplay(headlines()[currentHeadlineIndex()].timeRange) + ' summary in ' + countdown">
              <app-icon name="clock" [size]="12" />
              <span class="hidden sm:inline uppercase opacity-60">Next</span>
              <span class="tabular-nums">{{ countdown }}</span>
            </div>
          } @else {
            <div class="p-1 opacity-20 group-hover:opacity-40 transition-opacity">
              <app-icon name="gripVertical" [size]="16" class="text-white" />
            </div>
          }
        </div>

        <div class="p-5 [@media(max-height:620px)]:p-4 flex flex-col min-h-[160px] [@media(max-height:620px)]:min-h-[140px]">
          
          <!-- Header: Icon + Badge -->
          <div class="flex items-center gap-2 mb-4 [@media(max-height:620px)]:mb-3 lg:pl-5">
            <app-icon-v2 name="sparkles" size="18" [customClass]="getSparkleColor(headlines()[currentHeadlineIndex()].timeRange)" />
            <span class="text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded border"
                  [ngClass]="getTimeRangeColor(headlines()[currentHeadlineIndex()].timeRange)">
              {{ headlines()[currentHeadlineIndex()].timeRange }}
            </span>
          </div>

          <!-- Content: Horizontal Carousel -->
          <div class="relative flex-1 overflow-hidden">
            <div class="flex transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                 [style.transform]="'translateX(-' + (currentHeadlineIndex() * 100) + '%)'">
              
              @for(headline of headlines(); track headline.id; let i = $index) {
                <div class="min-w-full lg:pl-5 pr-4">
                  <h3 class="text-sm md:text-base [@media(max-height:620px)]:text-sm font-medium text-white leading-relaxed line-clamp-3"
                      [class.line-clamp-none]="expandedHeadlineIds().has(headline.id)">
                    {{ headline.headline }}
                  </h3>

                  <!-- Inline Expansion (Desktop Only) -->
                  @if(!isMobile()) {
                    <div 
                      class="grid transition-all duration-500 ease-in-out overflow-hidden"
                      [style.grid-template-rows]="expandedHeadlineIds().has(headline.id) ? '1fr' : '0fr'"
                      [class.opacity-100]="expandedHeadlineIds().has(headline.id)"
                      [class.opacity-0]="!expandedHeadlineIds().has(headline.id)"
                    >
                      <div class="min-h-0 transition-all duration-500">
                        <div class="pt-3 mt-3 border-t border-white/10">
                          <p class="text-xs md:text-sm text-white/80 font-normal leading-relaxed">
                            {{ headline.summary || headline.headline }}
                          </p>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Footer: View More + Pagination -->
          <div class="flex items-center justify-between mt-4 [@media(max-height:620px)]:mt-2 lg:pl-5">
              <button 
                (click)="handleViewMore(headlines()[currentHeadlineIndex()].id); $event.stopPropagation()"
                class="text-xs font-semibold text-emerald-400 hover:text-emerald-300 underline underline-offset-4 transition-colors cursor-pointer"
              >
                {{ !isMobile() && expandedHeadlineIds().has(headlines()[currentHeadlineIndex()].id) ? 'View Less' : 'View More' }}
              </button>

            <!-- Pagination Dots (Mobile Only) -->
            @if(isMobile()) {
              <div class="flex gap-1.5 items-center">
                @for(dot of headlines(); track $index; let i = $index) {
                  <button 
                    (click)="currentHeadlineIndex.set(i); $event.stopPropagation()"
                    class="w-1.5 h-1.5 rounded-full transition-all duration-300"
                    [ngClass]="i === currentHeadlineIndex() ? 'bg-white w-3 opacity-100' : 'bg-white/30 hover:bg-white/50'"
                  ></button>
                }
              </div>
            }
          </div>

        </div>
      </div>
      }

    }

    <!-- Full Screen Overlay (teleported to <body> so it escapes any ancestor
         stacking context on real iOS/Android devices and stays above the
         mobile header (z-5500) and bottom navigation (z-6000)). -->
    <ng-template #overlayTpl>
      <div class="ai-headlines-overlay fixed inset-0 z-[99999] flex items-start justify-center bg-black/40 animate-in fade-in duration-300 custom-scrollbar-hide overflow-y-auto"
           style="padding-top: calc(env(safe-area-inset-top) + 8rem); padding-bottom: max(env(safe-area-inset-bottom), 6rem); padding-left: 0.75rem; padding-right: 0.75rem;"
           (click)="showAllHeadlines.set(false)">
        <div class="w-full max-w-xl space-y-4 custom-scrollbar-hide" (click)="$event.stopPropagation()">

          @for(headline of headlines(); track headline.id) {
            <div class="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br shadow-2xl transition-all hover:scale-[1.01] duration-300"
                 [ngClass]="getOverlayGradient(headline.timeRange)">

              <button (click)="showAllHeadlines.set(false)" class="absolute top-4 right-4 text-white/50 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-full">
                <app-icon name="x" [size]="16" />
              </button>

              <div class="flex items-center gap-2 mb-3">
                <app-icon-v2 name="sparkles" size="16" [customClass]="getSparkleColor(headline.timeRange)" />
                <span class="text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded border border-white/20 bg-white/10 text-white shadow-sm">
                  {{ headline.timeRange }}
                </span>
              </div>

              <div class="space-y-2">
                <h2 class="text-base md:text-lg font-semibold text-white leading-tight">
                  {{ headline.headline }}
                </h2>
                <div class="h-px w-12 bg-white/20"></div>
                <p class="text-[11px] md:text-xs text-white/90 leading-relaxed font-normal">
                  {{ headline.summary || headline.headline }}
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    </ng-template>
  `
})
export class AiHeadlinesComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * Session-scoped in-memory cache for cron schedules, keyed by productId.
   * Survives component re-mounts and route changes but resets on a hard
   * refresh (because the JS runtime is reinitialised).
   */
  private static cronConfigCache = new Map<string, CronNextRun[]>();

  private apiService = inject(ApiService);
  public commonService = inject(CommonService);
  private router = inject(Router);
  private headlinePageService = inject(HeadlinePageService);
  public _breakpointService = inject(BreakpointService);
  private vcr = inject(ViewContainerRef);

  // Overlay teleport refs
  @ViewChild('overlayTpl', { static: true }) overlayTpl!: TemplateRef<unknown>;
  private overlayView: EmbeddedViewRef<unknown> | null = null;

  // State
  private rawHeadlines: any[] = [];
  headlines = signal<Headline[]>([]);
  currentHeadlineIndex = signal(0);
  isPaused = signal(false);

  // Configuration
  headlineDirection = signal<'horizontal' | 'vertical'>('horizontal');
  showHeadlineNavigation = signal(true);
  expandedHeadlineIds = signal<Set<string>>(new Set());
  showAllHeadlines = signal(false);
  isMobile = computed(() => !this._breakpointService.isDesktop());

  isLoading = signal(true);

  // Cron schedule state for the "next execution" countdown
  cronConfigs = signal<CronNextRun[]>([]);
  now = signal(Date.now());

  private touchStartX = 0;
  private touchStartY = 0;

  isDenefitsSelected = computed(() => {
    const selected = this.apiService.selectedProductId();
    const products = this.apiService.products();
    const denefits = products.find(p =>
      (p.pdidename?.toLowerCase() === 'denefits') ||
      (p.name?.toLowerCase() === 'denefits')
    );

    const result = !!(selected && denefits && selected === denefits.id);
    return result;
  });

  /**
   * Live, human-readable countdown to the next cron execution for the
   * currently visible headline's time range (e.g. "2d 4h", "12m 45s").
   * Returns null when no active cron schedule is known for that range.
   */
  nextRunCountdown = computed<string | null>(() => {
    const list = this.headlines();
    const current = list[this.currentHeadlineIndex()];
    if (!current) return null;

    const config = this.cronConfigs().find((c) => c.cronType === current.timeRange);
    if (!config?.nextScheduledRun) return null;

    const target = new Date(config.nextScheduledRun).getTime();
    if (Number.isNaN(target)) return null;

    return this.formatCountdown(target - this.now());
  });

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.mapHeadlines();
    });


    effect((onCleanup) => {
      const hasExpanded = this.expandedHeadlineIds().size > 0;
      const isOverlayOpen = this.showAllHeadlines();
      
      if (this.headlines().length > 1 && !this.isPaused() && !this.isLoading() && !hasExpanded && !isOverlayOpen) {
        const intervalId = setInterval(() => {
          this.nextHeadline();
        }, 5000);

        onCleanup(() => {
          clearInterval(intervalId);
        });
      }
    });

    effect(() => {
      if (this.apiService.selectedProductId()) {
        this.fetchHeadlines();
      }
    });

    // Fetch active cron schedules once both the selected product and the
    // product list (needed to resolve the product PID) are available.
    effect(() => {
      const productId = this.apiService.selectedProductId();
      const products = this.apiService.products();
      if (productId && products.length > 0) {
        this.fetchCronConfigs(productId, products);
      }
    });

    // Tick every second to drive the live countdown, but only while there
    // is at least one known schedule and headlines are rendered.
    effect((onCleanup) => {
      const hasSchedule = this.cronConfigs().length > 0 && this.headlines().length > 0;
      if (hasSchedule && !this.isLoading()) {
        const intervalId = setInterval(() => {
          this.now.set(Date.now());
        }, 1000);

        onCleanup(() => clearInterval(intervalId));
      }
    });

    // Teleport the full-screen overlay into <body> so its z-index is not
    // trapped inside any ancestor stacking context (real iOS/Android issue
    // where the overlay was hiding under the mobile header / bottom nav).
    effect(() => {
      const open = this.showAllHeadlines() && this.isDenefitsSelected();
      if (open) {
        this.openOverlay();
      } else {
        this.closeOverlay();
      }
    });
  }

  ngOnInit(): void {
    this.apiService.fetchProducts().subscribe();
  }

  ngAfterViewInit(): void {
    // Template ref is now available; nothing else needed here, the effect
    // in the constructor will mount it on demand.
  }

  ngOnDestroy(): void {
    this.closeOverlay();
  }

  private openOverlay(): void {
    if (this.overlayView || !this.overlayTpl) return;
    this.overlayView = this.vcr.createEmbeddedView(this.overlayTpl);
    this.overlayView.detectChanges();
    for (const node of this.overlayView.rootNodes) {
      if (node instanceof HTMLElement || node instanceof Node) {
        document.body.appendChild(node);
      }
    }
  }

  private closeOverlay(): void {
    if (!this.overlayView) return;
    for (const node of this.overlayView.rootNodes) {
      if (node && (node as Node).parentNode) {
        (node as Node).parentNode!.removeChild(node as Node);
      }
    }
    this.overlayView.destroy();
    this.overlayView = null;
  }

  private fetchHeadlines() {
    this.isLoading.set(true);
    const payload = { productId: this.apiService.selectedProductId() }

    this.apiService.get('summary/headlines/latest', payload).subscribe({
      next: (res: any) => {
        // Flexible check for headlines array (handle res.data.headlines or res.headlines)
        const headlinesArray = res?.data?.headlines || res?.headlines;

        if (Array.isArray(headlinesArray)) {
          this.rawHeadlines = headlinesArray;
          this.mapHeadlines();
        } else {
          console.warn('AiHeadlines: Invalid API response structure (no headlines array)', res);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('AiHeadlines: API Error', err);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Fetch active cron configurations for the selected product and store a
   * minimal view-model (cronType + nextScheduledRun) used by the countdown.
   *
   * Uses an in-memory static cache keyed by productId so the API is hit
   * only once per session per product. A hard refresh resets the cache
   * (JS runtime reload), and switching products fetches fresh data.
   *
   * @param productId Currently selected product id from ApiService.
   * @param products Loaded product list used to resolve the numeric PID.
   * @returns void
   */
  private fetchCronConfigs(productId: string, products: any[]) {
    const cached = AiHeadlinesComponent.cronConfigCache.get(productId);
    if (cached) {
      this.cronConfigs.set(cached);
      return;
    }

    const selectedProduct = products.find((p) => p.id === productId);
    const productPid = selectedProduct?.pid;

    const params: Record<string, any> = { isActive: true, limit: 100 };
    if (productPid != null) {
      params['productPid'] = productPid;
    }

    this.apiService.get('summary/cron-configs', params).subscribe({
      next: (res: any) => {
        const configs = res?.data?.configs || res?.configs;
        if (Array.isArray(configs)) {
          const mapped: CronNextRun[] = configs.map((c: any) => ({
            cronType: c.cronType,
            nextScheduledRun: c.nextScheduledRun ?? null,
          }));
          AiHeadlinesComponent.cronConfigCache.set(productId, mapped);
          this.cronConfigs.set(mapped);
        }
      },
      error: (err) => {
        console.error('AiHeadlines: Cron config API Error', err);
      },
    });
  }

  /**
   * Format a millisecond duration into a compact countdown label.
   * @param ms Milliseconds remaining until the next execution.
   * @returns A short label like "2d 4h", "5h 23m", "12m 45s" or "Running now".
   */
  private formatCountdown(ms: number): string {
    if (ms <= 0) return 'Running now';

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  private mapHeadlines() {
    const fullUrl = this.router.url;
    const baseUrl = fullUrl.split('?')[0];
    const configs = this.headlinePageService.getAllConfigs();



    // Try specific match (with query params) first
    let config = configs.find(c => c.route.includes('?') && (fullUrl.endsWith(c.route) || fullUrl.includes(c.route)));

    // Fallback to base URL match if no specific match found
    if (!config) {
      config = configs.find(c => !c.route.includes('?') && (baseUrl.endsWith(c.route) || baseUrl.includes(c.route)));
    }



    if (!config || !this.rawHeadlines.length) {
      if (!config && this.rawHeadlines.length > 0) {
        // console.warn('AiHeadlines: No config found for route', fullUrl);
      }
      this.headlines.set([]);
      return;
    }

    const mappedHeadlines: Headline[] = this.rawHeadlines.map((item: any, index: number) => {
      const pageHeadline = item.headlines?.[config.key];

      return {
        id: item.pid?.toString() || index.toString(),
        timeRange: item.timeRange as any,
        periodStart: item.periodStart,
        periodEnd: item.periodEnd,
        title: pageHeadline?.headline || 'Highlights',
        headline: pageHeadline?.headline || 'No headline available for this period.',
        summary: pageHeadline?.summary || '',
      };
    }).filter(h => h.headline !== 'No headline available for this period.');

    this.headlines.set(mappedHeadlines);

    if (this.currentHeadlineIndex() >= mappedHeadlines.length) {
      this.currentHeadlineIndex.set(0);
    }
  }

  nextHeadline() {
    // 1. Reset expansions immediately
    this.expandedHeadlineIds.set(new Set());

    // 2. Proceed to next slide
    const length = this.headlines().length;
    const nextIndex = (this.currentHeadlineIndex() + 1) % length;
    this.currentHeadlineIndex.set(nextIndex);
  }

  prevHeadline() {
    // 1. Reset expansions immediately
    this.expandedHeadlineIds.set(new Set());

    // 2. Proceed to previous slide
    const length = this.headlines().length;
    const prevIndex = (this.currentHeadlineIndex() - 1 + length) % length;
    this.currentHeadlineIndex.set(prevIndex);
  }

  // --------------------------------

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isPaused.set(true);
  }

  onTouchEnd(event: TouchEvent) {
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;
    const deltaX = touchEndX - this.touchStartX;
    const deltaY = touchEndY - this.touchStartY;

    // Minimum swipe distance of 50px and ensure it's primarily horizontal
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) {
        this.prevHeadline();
      } else {
        this.nextHeadline();
      }
    }
    this.isPaused.set(false);
  }

  handleViewMore(id: string) {
    if (this.isMobile()) {
      this.showAllHeadlines.set(true);
    } else {
      this.toggleDetails(id);
    }
  }

  toggleDetails(id: string) {
    this.expandedHeadlineIds.update(ids => {
      const newIds = new Set(ids);
      if (newIds.has(id)) {
        newIds.delete(id);
      } else {
        newIds.add(id);
      }
      return newIds;
    });
  }

  getTimeRangeColor(timeRange: string): string {
    const colors: Record<string, string> = {
      'DAILY': 'bg-blue-500/20 text-blue-300 border-blue-500/20',
      'WEEKLY': 'bg-purple-500/20 text-purple-300 border-purple-500/20',
      'MONTHLY': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/20',
      'QUARTERLY': 'bg-orange-500/20 text-orange-300 border-orange-500/20',
    };
    return colors[timeRange] || 'bg-white/10 text-white/70 border-white/10';
  }

  getSparkleColor(timeRange: string): string {
    const colors: Record<string, string> = {
      'DAILY': 'text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]',
      'WEEKLY': 'text-purple-400 drop-shadow-[0_0_8px_rgba(192,132,252,0.5)]',
      'MONTHLY': 'text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]',
      'QUARTERLY': 'text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.5)]',
    };
    return colors[timeRange] || 'text-white/70';
  }

  getOverlayGradient(timeRange: string): string {
    const gradients: Record<string, string> = {
      'DAILY': 'from-indigo-900 to-purple-800',
      'WEEKLY': 'from-purple-900 to-fuchsia-900',
      'MONTHLY': 'from-emerald-800 to-teal-900',
      'QUARTERLY': 'from-orange-800 to-red-900',
    };
    return gradients[timeRange] || 'from-gray-800 to-gray-900';
  }

  getTimePeriodDisplay(timeRange: string): string {
    const map: Record<string, string> = {
      'DAILY': 'day',
      'WEEKLY': 'week',
      'MONTHLY': 'month',
      'QUARTERLY': 'quarter',
      'YEARLY': 'year'
    };
    return map[timeRange] || 'period';
  }
}